import React, { useEffect, useState } from 'react';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import { Box, Typography, Switch } from '@material-ui/core';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import DeleteIcon from '@mui/icons-material/Delete';
import SnackbarOmni from 'allGlobalComponents/SnackbarOmni';
import TableOmniNewAnimated from 'allGlobalComponents/TableOmniNewAnimated';
import ButtonOmniNew from 'allGlobalComponents/ButtonOmniNew';
import ModalOmni from 'allGlobalComponents/ModalOmni';
import ButtonOmni from 'allGlobalComponents/ButtonOmni';
import FormFieldOmni from 'allGlobalComponents/FormFieldOmni';
import SearchableDropdown from 'allGlobalComponents/SearchableDropdown';
import DateFilterWithExport from 'allGlobalComponents/DateFilterWithExport';
import { useSnackbar, usePagination, useApi } from 'app/hooks';

const useStyles = makeStyles(theme => ({
    formContent: {
        padding: theme.spacing(3),
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(1.5)
    },
    buttonContainer: {
        width: '100%',
        display: 'flex',
        justifyContent: 'flex-end',
        gap: theme.spacing(2)
    },
    switch: {
        '& .MuiSwitch-switchBase': {
            color: theme.palette.type === 'dark' ? '#bdbdbd' : undefined
        },
        '& .MuiSwitch-track': {
            backgroundColor: theme.palette.type === 'dark' ? '#757575' : undefined
        }
    },
    toggleContent: {
        padding: theme.spacing(3),
        textAlign: 'left'
    }
}));

const QUALITY_OPTIONS = [
    { value: 'GREEN', label: 'Green' },
    { value: 'YELLOW', label: 'Yellow' },
    { value: 'RED', label: 'Red' }
];

const PROVIDER_OPTIONS = [
    { value: 'whatsapp_cloud', label: 'WhatsApp Cloud' },
    { value: 'routemobile', label: 'Route Mobile' }
];

const INITIAL_FORM = {
    clientId: '',
    metaAppId: '',
    phoneNumber: '',
    phoneNumberId: '',
    accessToken: '',
    wabaId: '',
    provider: 'whatsapp_cloud',
    displayName: '',
    qualityRating: '',
    messageLimit: '',
    username: '',
    usernameStatus: '',
    configPairs: []
};

function WhatsAppAccountsTab() {
    const classes = useStyles();
    const theme = useTheme();

    // Hooks
    const { snackbar, showSuccess, showError, showWarning } = useSnackbar();
    const { currentParams, setCurrentParams, totalItems, setTotalItems, resetPage } = usePagination(10);
    const { isLoading, request } = useApi();

    // State
    const [data, setData] = useState([]);
    const [clients, setClients] = useState([]);
    const [metaApps, setMetaApps] = useState([]);
    const [createOpen, setCreateOpen] = useState(false);
    const [editOpen, setEditOpen] = useState(false);
    const [selectedRow, setSelectedRow] = useState(null);
    const [formData, setFormData] = useState(INITIAL_FORM);
    const [saving, setSaving] = useState(false);
    const [togglingId, setTogglingId] = useState(null);
    const [toggleOpen, setToggleOpen] = useState(false);

    // Filter state
    const [filterValues, setFilterValues] = useState({
        clientId: '',
        isActive: '',
        provider: '',
        status: ''
    });
    const [refreshKey, setRefreshKey] = useState(0);

    // Fetch clients
    const fetchClients = () => {
        request({
            module: 'Business',
            method: 'get_pagination',
            payload: {
                page: 0,
                limit: 1000,
                columns: '*',
                sortby: 'ASC',
                orderby: 'id',
                where: 'id != $1 AND displayed = false order by id',
                values: 0
            },
            onSuccess: (response) => {
                setClients(response.data.data.clients.clients || []);
            },
            onError: (message) => showError(message || 'Error fetching clients')
        });
    };

    // Fetch meta apps for dropdown
    const fetchMetaApps = () => {
        request({
            module: 'saChannels',
            method: 'metaAppList',
            payload: {},
            onSuccess: (response) => {
                const apps = response.data.data.data || [];
                setMetaApps(apps);
            },
            onError: (message) => showError(message || 'Error fetching meta apps')
        });
    };

    // Fetch WhatsApp accounts
    const fetchAccounts = () => {
        const payload = {};

        if (filterValues.clientId) payload.clientId = filterValues.clientId;
        if (filterValues.isActive !== '') payload.isActive = filterValues.isActive;
        if (filterValues.provider) payload.provider = filterValues.provider;
        if (filterValues.status) payload.status = filterValues.status;

        request({
            module: 'saChannels',
            method: 'whatsappAccountsGetAllAccounts',
            payload,
            onSuccess: (response) => {
                const accounts = response.data.data.data || [];
                setData(accounts);
                setTotalItems(accounts.length);
            },
            onError: (message) => {
                showError(message || 'Error fetching WhatsApp accounts');
                setData([]);
                setTotalItems(0);
            }
        });
    };

    useEffect(() => {
        fetchClients();
        fetchMetaApps();
    }, []);

    useEffect(() => {
        fetchAccounts();
    }, [filterValues, refreshKey]);

    // Dropdown options
    const clientOptions = clients
        .map(c => ({
            value: c.id.toString(),
            label: c.comapny || `${c.first_name || ''} ${c.last_name || ''}`.trim() || `Client ${c.id}`
        }))
        .sort((a, b) => a.label?.localeCompare(b.label, undefined, { sensitivity: 'base' })) || [];

    const metaAppOptions = metaApps.map(app => ({
        value: app.id.toString(),
        label: app.name
    }));

    // Form handlers
    const handleFormChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    const resetForm = () => {
        setFormData(INITIAL_FORM);
    };

    // Build payload
    const buildPayload = () => {
        if (!formData.clientId || !formData.metaAppId || !formData.phoneNumber ||
            !formData.phoneNumberId || !formData.accessToken || !formData.wabaId ||
            !formData.provider || !formData.displayName) {
            showWarning('Please fill in all required fields');
            return null;
        }

        let configuration = null;
        if (formData.configPairs.length > 0) {
            configuration = {};
            for (const pair of formData.configPairs) {
                if (!pair.key.trim() || !pair.value.trim()) {
                    showWarning('Please fill in all configuration key-value pairs or remove empty ones');
                    return null;
                }
                configuration[pair.key.trim()] = pair.value.trim();
            }
        }

        const payload = {
            clientId: formData.clientId,
            metaAppId: formData.metaAppId,
            phoneNumber: formData.phoneNumber,
            phoneNumberId: formData.phoneNumberId,
            accessToken: formData.accessToken,
            wabaId: formData.wabaId,
            provider: formData.provider,
            displayName: formData.displayName
        };

        if (formData.qualityRating) payload.qualityRating = formData.qualityRating;
        if (formData.messageLimit) payload.messageLimit = formData.messageLimit;
        if (formData.username) payload.username = formData.username;
        if (formData.usernameStatus) payload.usernameStatus = formData.usernameStatus;
        if (configuration) payload.configuration = configuration;

        return payload;
    };

    // Create
    const handleCreate = () => {
        const payload = buildPayload();
        if (!payload) return;

        setSaving(true);
        request({
            module: 'saChannels',
            method: 'whatsappAccountsAdd',
            payload,
            onSuccess: (response) => {
                showSuccess(response.data.data.message || 'WhatsApp account added successfully');
                setCreateOpen(false);
                resetForm();
                fetchAccounts();
                setSaving(false);
            },
            onError: (message) => {
                showError(message || 'Error adding WhatsApp account');
                setSaving(false);
            },
            onFinally: () => setSaving(false)
        });
    };

    // Edit open
    const handleEditOpen = (row) => {
        setSelectedRow(row);

        let configPairs = [];
        if (row.configuration && typeof row.configuration === 'object') {
            configPairs = Object.entries(row.configuration).map(([key, value]) => ({
                key,
                value: String(value)
            }));
        }

        setFormData({
            clientId: row.client_id?.toString() || '',
            metaAppId: row.meta_app_id?.toString() || '',
            phoneNumber: row.phone_number || '',
            phoneNumberId: row.phone_number_id || '',
            accessToken: row.access_token || '',
            wabaId: row.waba_id || '',
            provider: row.provider || 'whatsapp_cloud',
            displayName: row.display_name || '',
            qualityRating: row.quality_rating || '',
            messageLimit: row.message_limit?.toString() || '',
            username: row.username || '',
            usernameStatus: row.username_status || '',
            configPairs
        });
        setEditOpen(true);
    };

    // Update
    const handleUpdate = () => {
        const payload = buildPayload();
        if (!payload) return;

        payload.id = selectedRow.id;

        setSaving(true);
        request({
            module: 'saChannels',
            method: 'whatsappAccountsUpdate',
            payload,
            onSuccess: (response) => {
                showSuccess(response.data.data.message || 'WhatsApp account updated successfully');
                setEditOpen(false);
                resetForm();
                setSelectedRow(null);
                fetchAccounts();
                setSaving(false);
            },
            onError: (message) => {
                showError(message || 'Error updating WhatsApp account');
                setSaving(false);
            },
            onFinally: () => setSaving(false)
        });
    };

    // Toggle - open confirmation
    const handleToggleOpen = (row) => {
        setSelectedRow(row);
        setToggleOpen(true);
    };

    // Toggle - confirm
    const handleToggleConfirm = () => {
        setTogglingId(selectedRow.id);
        setToggleOpen(false);
        request({
            module: 'saChannels',
            method: 'whatsappAccountsToggle',
            payload: { clientId: selectedRow.client_id, id: selectedRow.id },
            onSuccess: (response) => {
                showSuccess(response.data.data.message || 'WhatsApp account toggled successfully');
                fetchAccounts();
                setTogglingId(null);
                setSelectedRow(null);
            },
            onError: (message) => {
                showError(message || 'Error toggling WhatsApp account');
                setTogglingId(null);
                setSelectedRow(null);
            },
            onFinally: () => setTogglingId(null)
        });
    };

    // Config pair handlers
    const handleAddConfigPair = () => {
        setFormData(prev => ({
            ...prev,
            configPairs: [...prev.configPairs, { key: '', value: '' }]
        }));
    };

    const handleRemoveConfigPair = (index) => {
        setFormData(prev => ({
            ...prev,
            configPairs: prev.configPairs.filter((_, i) => i !== index)
        }));
    };

    const handleConfigPairChange = (index, field, value) => {
        setFormData(prev => ({
            ...prev,
            configPairs: prev.configPairs.map((pair, i) =>
                i === index ? { ...pair, [field]: value } : pair
            )
        }));
    };

    const isFormInvalid = () => {
        const hasEmptyConfigPairs = formData.configPairs.some(
            pair => !pair.key.trim() || !pair.value.trim()
        );
        return (
            !formData.clientId ||
            !formData.metaAppId ||
            !formData.phoneNumber.trim() ||
            !formData.phoneNumberId.trim() ||
            !formData.accessToken.trim() ||
            !formData.wabaId.trim() ||
            !formData.provider ||
            !formData.displayName.trim() ||
            saving ||
            hasEmptyConfigPairs
        );
    };

    // Filter handlers
    const handleFiltersApply = (filters) => {
        setFilterValues(filters);
        resetPage();
    };

    const handleFiltersClear = (filters) => {
        setFilterValues(filters);
        resetPage();
    };

    const handleRefresh = () => {
        setRefreshKey(prev => prev + 1);
    };

    // Filter fields config
    const filterFields = [
        {
            name: 'clientId',
            label: 'Client',
            type: 'searchable',
            options: clientOptions,
            defaultValue: '',
            placeholder: 'Search clients...'
        },
        {
            name: 'provider',
            label: 'Provider',
            type: 'select',
            options: [
                { value: '', label: 'All' },
                { value: 'whatsapp_cloud', label: 'WhatsApp Cloud' },
                { value: 'route_mobile', label: 'Route Mobile' }
            ],
            defaultValue: ''
        },
        {
            name: 'status',
            label: 'Status',
            type: 'select',
            options: [
                { value: '', label: 'All' },
                { value: 'active', label: 'Active' },
                { value: 'disconnected', label: 'Disconnected' },
                { value: 'banned', label: 'Banned' },
                { value: 'pending', label: 'Pending' }
            ],
            defaultValue: ''
        },
        {
            name: 'isActive',
            label: 'Active',
            type: 'select',
            options: [
                { value: '', label: 'All' },
                { value: 'true', label: 'Active' },
                { value: 'false', label: 'Inactive' }
            ],
            defaultValue: ''
        }
    ];

    // Table columns
    const headCells = [
        { id: 'id', numeric: false, disablePadding: false, label: 'ID' },
        { id: 'client_id', numeric: false, disablePadding: false, label: 'Client Id' },
        {
            id: 'client_name',
            numeric: false,
            disablePadding: false,
            label: 'Client',
            customRow: row => `${row.client_first_name || ''} ${row.client_last_name || ''}`.trim() || '-'
        },
        { id: 'display_name', numeric: false, disablePadding: false, label: 'Display Name', tooltip: true },
        { id: 'meta_app_name', numeric: false, disablePadding: false, label: 'Meta App', tooltip: true },
        { id: 'phone_number_id', numeric: false, disablePadding: false, label: 'Phone Number Id', tooltip: true },
        { id: 'phone_number', numeric: false, disablePadding: false, label: 'Phone Number', tooltip: true },
        { id: 'waba_id', numeric: false, disablePadding: false, label: 'WABA ID', tooltip: true },
        { id: 'provider', numeric: false, disablePadding: false, label: 'Provider' },
        {
            id: 'quality_rating',
            numeric: false,
            disablePadding: false,
            label: 'Quality Rating',
            pill: true,
            pillConfig: {
                mapping: {
                    GREEN: {
                        label: 'Green',
                        color: '#4caf50',
                        bgColor: 'rgba(76, 175, 80, 0.1)'
                    },
                    YELLOW: {
                        label: 'Yellow',
                        color: '#ff9800',
                        bgColor: 'rgba(255, 152, 0, 0.1)'
                    },
                    RED: {
                        label: 'Red',
                        color: '#f44336',
                        bgColor: 'rgba(244, 67, 54, 0.1)'
                    }
                },
                defaultColor: '#9e9e9e',
                defaultBgColor: 'rgba(158, 158, 158, 0.1)'
            }
        },
        { id: 'message_limit', numeric: false, disablePadding: false, label: 'Msg Limit' },
        { id: 'username', numeric: false, disablePadding: false, label: 'Username', tooltip: true },
        { id: 'username_status', numeric: false, disablePadding: false, label: 'Username Status' },
        {
            id: 'is_active',
            numeric: false,
            disablePadding: false,
            label: 'Status',
            pill: true,
            pillConfig: {
                mapping: {
                    true: {
                        label: 'Active',
                        color: '#4caf50',
                        bgColor: 'rgba(76, 175, 80, 0.1)',
                        icon: <CheckCircleIcon fontSize="small" />
                    },
                    false: {
                        label: 'Inactive',
                        color: '#f44336',
                        bgColor: 'rgba(244, 67, 54, 0.1)',
                        icon: <CancelIcon fontSize="small" />
                    }
                },
                defaultColor: '#9e9e9e',
                defaultBgColor: 'rgba(158, 158, 158, 0.1)'
            }
        },
        { id: 'dt', numeric: false, disablePadding: false, label: 'Created', dt: true },
        { id: 'dtu', numeric: false, disablePadding: false, label: 'Updated', dt: true },
        { id: 'edit', numeric: false, disablePadding: false, label: 'Edit' },
        {
            id: 'toggle',
            numeric: false,
            disablePadding: false,
            label: 'Active',
            customRow: row => (
                <Switch
                    checked={row.is_active === true}
                    onChange={() => handleToggleOpen(row)}
                    disabled={togglingId === row.id}
                    className={classes.switch}
                    size="small"
                />
            )
        }
    ];

    // Form content
    const renderFormContent = () => {
        const selectedClientOption = clientOptions.find(opt => opt.value === formData.clientId) || null;

        return (
            <div className={classes.formContent}>
                <SearchableDropdown
                    label="Client *"
                    options={clientOptions}
                    value={selectedClientOption}
                    onChange={(newValue) => handleFormChange('clientId', newValue?.value || '')}
                    placeholder="Search clients..."
                    style={{ width: '100%' }}
                />
                <FormFieldOmni
                    label="Meta App *"
                    variant="outlined"
                    value={formData.metaAppId}
                    onChange={(e) => handleFormChange('metaAppId', e.target.value)}
                    type="select"
                    options={metaAppOptions}
                    size="small"
                    fullWidth
                    placeholder="Select meta app"
                />
                <FormFieldOmni
                    label="Display Name *"
                    variant="outlined"
                    value={formData.displayName}
                    onChange={(e) => handleFormChange('displayName', e.target.value)}
                    type="text"
                    size="small"
                    fullWidth
                    placeholder="e.g. My WhatsApp Business"
                />
                <FormFieldOmni
                    label="Phone Number (Display) *"
                    variant="outlined"
                    value={formData.phoneNumber}
                    onChange={(e) => handleFormChange('phoneNumber', e.target.value)}
                    type="text"
                    size="small"
                    fullWidth
                    placeholder="e.g. 920000000000"
                />
                <FormFieldOmni
                    label="Phone Number ID *"
                    variant="outlined"
                    value={formData.phoneNumberId}
                    onChange={(e) => handleFormChange('phoneNumberId', e.target.value)}
                    type="text"
                    size="small"
                    fullWidth
                    placeholder="e.g. 000000000000000"
                />
                <FormFieldOmni
                    label="Access Token *"
                    variant="outlined"
                    value={formData.accessToken}
                    onChange={(e) => handleFormChange('accessToken', e.target.value)}
                    type="text"
                    size="small"
                    fullWidth
                    placeholder="Bearer token from configuration"
                />
                <FormFieldOmni
                    label="WABA ID *"
                    variant="outlined"
                    value={formData.wabaId}
                    onChange={(e) => handleFormChange('wabaId', e.target.value)}
                    type="text"
                    size="small"
                    fullWidth
                    placeholder="e.g. 0000000000000000"
                />
                <FormFieldOmni
                    label="Provider *"
                    variant="outlined"
                    value={formData.provider}
                    onChange={(e) => handleFormChange('provider', e.target.value)}
                    type="select"
                    options={PROVIDER_OPTIONS}
                    size="small"
                    fullWidth
                />
                <FormFieldOmni
                    label="Quality Rating"
                    variant="outlined"
                    value={formData.qualityRating}
                    onChange={(e) => handleFormChange('qualityRating', e.target.value)}
                    type="select"
                    options={[{ value: '', label: 'Select' }, ...QUALITY_OPTIONS]}
                    size="small"
                    fullWidth
                />
                <FormFieldOmni
                    label="Message Limit"
                    variant="outlined"
                    value={formData.messageLimit}
                    onChange={(e) => handleFormChange('messageLimit', e.target.value)}
                    type="text"
                    size="small"
                    fullWidth
                    placeholder="e.g. 10000"
                />
                <FormFieldOmni
                    label="Username"
                    variant="outlined"
                    value={formData.username}
                    onChange={(e) => handleFormChange('username', e.target.value)}
                    type="text"
                    size="small"
                    fullWidth
                    placeholder="Optional"
                />
                <FormFieldOmni
                    label="Username Status"
                    variant="outlined"
                    value={formData.usernameStatus}
                    onChange={(e) => handleFormChange('usernameStatus', e.target.value)}
                    type="select"
                    options={[
                        { value: '', label: 'Select' },
                        { value: 'not_set', label: 'Not Set' },
                        { value: 'reserved', label: 'Reserved' },
                        { value: 'pending', label: 'Pending' },
                        { value: 'active', label: 'Active' }
                    ]}
                    size="small"
                    fullWidth
                />

                {/* Configuration key-value pairs */}
                <Box display="flex" alignItems="center" justifyContent="space-between" style={{ marginTop: 8 }}>
                    <Typography variant="subtitle1" style={{ fontWeight: 600 }} color="textPrimary">
                        Configuration
                    </Typography>
                    <ButtonOmni
                        onClick={handleAddConfigPair}
                        variant="outlined"
                        label="+ Add"
                        size="small"
                    />
                </Box>
                {formData.configPairs.map((pair, index) => (
                    <Box key={index} display="flex" alignItems="center" gap={1} style={{ gap: 8 }}>
                        <FormFieldOmni
                            label="Key"
                            variant="outlined"
                            value={pair.key}
                            onChange={(e) => handleConfigPairChange(index, 'key', e.target.value)}
                            type="text"
                            size="small"
                            fullWidth
                            placeholder="e.g. api_url"
                        />
                        <FormFieldOmni
                            label="Value"
                            variant="outlined"
                            value={pair.value}
                            onChange={(e) => handleConfigPairChange(index, 'value', e.target.value)}
                            type="text"
                            size="small"
                            fullWidth
                            placeholder="e.g. https://example.com"
                        />
                        <DeleteIcon
                            fontSize="large"
                            style={{ cursor: 'pointer', color: theme.palette.secondary.value, flexShrink: 0 }}
                            onClick={() => handleRemoveConfigPair(index)}
                        />
                    </Box>
                ))}
            </div>
        );
    };

    // Create dialog actions
    const createActions = (
        <div className={classes.buttonContainer}>
            <ButtonOmni onClick={() => { setCreateOpen(false); resetForm(); }} variant="outlined" label="Cancel" disabled={saving} />
            <ButtonOmni onClick={handleCreate} variant="outlined" label={saving ? 'Creating...' : 'Create'} disabled={isFormInvalid()} />
        </div>
    );

    // Edit dialog actions
    const editActions = (
        <div className={classes.buttonContainer}>
            <ButtonOmni onClick={() => { setEditOpen(false); resetForm(); setSelectedRow(null); }} variant="outlined" label="Cancel" disabled={saving} />
            <ButtonOmni onClick={handleUpdate} variant="outlined" label={saving ? 'Updating...' : 'Update'} disabled={isFormInvalid()} />
        </div>
    );

    return (
        <div className="w-full mx-auto">
            {/* Filters & Create */}
            <DateFilterWithExport
                showDateRange={false}
                showExport={false}
                filterFields={filterFields}
                filterValues={filterValues}
                onFiltersApply={handleFiltersApply}
                onFiltersClear={handleFiltersClear}
                showRefresh={true}
                onRefresh={handleRefresh}
                customRightContent={
                    <ButtonOmniNew
                        buttonType="create"
                        label="Add WhatsApp Account"
                        onClick={() => { resetForm(); setCreateOpen(true); }}
                        variant="outlined"
                    />
                }
            />

            {/* Table  */}
            <TableOmniNewAnimated
                rows={data}
                headCells={headCells}
                perPageRows={currentParams.limit}
                setCurrentParams={setCurrentParams}
                totalItems={totalItems}
                curPage={currentParams.page}
                loading={isLoading}
                onEditClick={handleEditOpen}
            />

            {/* Create Dialog */}
            <ModalOmni
                disableBackdropClick
                open={createOpen}
                handleClose={() => { setCreateOpen(false); resetForm(); }}
                title="Add WhatsApp Account"
                content={renderFormContent()}
                actions={createActions}
                customWidth={550}
            />

            {/* Edit Dialog */}
            <ModalOmni
                disableBackdropClick
                open={editOpen}
                handleClose={() => { setEditOpen(false); resetForm(); setSelectedRow(null); }}
                title="Edit WhatsApp Account"
                content={renderFormContent()}
                actions={editActions}
                customWidth={550}
            />

            {/* Toggle Confirmation Dialog */}
            <ModalOmni
                disableBackdropClick
                open={toggleOpen}
                handleClose={() => { setToggleOpen(false); setSelectedRow(null); }}
                title={selectedRow?.is_active ? 'Deactivate WhatsApp Account' : 'Activate WhatsApp Account'}
                content={
                    <div className={classes.toggleContent}>
                        <Typography variant="body1" color="textPrimary">
                            Are you sure you want to {selectedRow?.is_active ? 'deactivate' : 'activate'} the WhatsApp account "<strong>{selectedRow?.display_name || ''}</strong>" ({selectedRow?.phone_number || ''})?
                        </Typography>
                    </div>
                }
                actions={
                    <div className={classes.buttonContainer}>
                        <ButtonOmni
                            onClick={() => { setToggleOpen(false); setSelectedRow(null); }}
                            variant="outlined"
                            label="Cancel"
                        />
                        <ButtonOmni
                            onClick={handleToggleConfirm}
                            variant="outlined"
                            label={selectedRow?.is_active ? 'Deactivate' : 'Activate'}
                        />
                    </div>
                }
                customWidth={450}
            />

            <SnackbarOmni {...snackbar} />
        </div>
    );
}

export default React.memo(WhatsAppAccountsTab);